// ADR-0140 §4 — consultas del propietario (fase 1, corte 7, ticket #0727)
// y ADR-0145 — la consulta es un hilo en nuestra base (fase 2, corte 1,
// ticket #0738). ADR-0155 — el propietario responde en su consulta (fase
// 3, ticket #0753).
//
// Lado del propietario (PWA), siempre por la unidad seleccionada (ADR-0143):
//   GET  /assets/:id/inquiries/availability → ¿hay alguien que la reciba?
//   POST /assets/:id/inquiries              → crea la consulta y avisa por correo
//   GET  /assets/:id/inquiries              → sus consultas sobre la unidad
//   GET  /assets/:id/inquiries/:inquiryId   → el hilo (consulta + mensajes)
//   POST /assets/:id/inquiries/:inquiryId/messages → vuelve a escribir (ADR-0155)
//   POST /assets/:id/inquiries/:inquiryId/read → marca «hasta aquí leí»
//
// Lado del staff (dashboard; jefe_proyecto en sus proyectos, gerente_postventa
// en toda la org):
//   GET  /owner-inquiries?assetGroupId=     → la bandeja del proyecto
//   GET  /owner-inquiries/:id               → el hilo
//   POST /owner-inquiries/:id/messages      → responde (Idempotency-Key)
//   POST /owner-inquiries/:id/read          → marca «hasta aquí leí»
//
// La creación requiere `Idempotency-Key` (reintentar no duplica la consulta
// ni el correo; la clave se guarda en la fila). Sólo texto, sin adjuntos.
// Desde la fase 2 la respuesta del staff vive en la app: el propietario
// recibe un aviso por correo y lee la respuesta en Consultas. Desde la
// fase 3 el propietario contesta en el mismo hilo (sólo texto).
//
// Sin destinatario (proyecto sin jefe válido y org sin gerente de
// postventa activo) la PWA no muestra «Consulta» y el POST responde 409
// INQUIRY_NO_RECIPIENT sin guardar nada. Si el correo no le llega a
// nadie, el POST responde 503 INQUIRY_NOT_DELIVERED: no hay promesa de
// fecha ni acuse, y el propietario reintenta con la misma clave.
import { z } from "zod";
import { findingTimelineActorSchema } from "./findings.js";
import { fechaCompromisoRespuesta } from "./observation-labels.js";
import { ownerNoticeOutcomeSchema } from "./owner-notifications.js";
import { OWNER_INQUIRY_ATTACHMENTS_PER_MESSAGE_MAX, ownerInquiryAttachmentRefSchema, ownerInquiryAttachmentSchema, } from "./owner-inquiry-attachments.js";
/** Tope de caracteres de una consulta y de cada respuesta (decisión del
 *  fundador 2026-10-15). */
export const OWNER_INQUIRY_BODY_MAX = 2000;
/** Roles de la inmobiliaria que pueden recibir una consulta como «jefe»
 *  del proyecto (asset_groups.supervisor_user_id) y, desde la fase 2, ver
 *  y responder la bandeja. Un supervisor_user_id con otro rol
 *  (propietario, inspector, un rol de facility) o sin membresía activa
 *  no cuenta, y la consulta cae a los gerentes. */
export const OWNER_INQUIRY_STAFF_ROLES = ["jefe_proyecto", "gerente_postventa"];
/** ¿Puede este rol ver y responder la bandeja de consultas? Sólo decide
 *  el rol; el alcance (qué proyectos) lo decide `loadScopedAssets`. */
export function isOwnerInquiryStaffRole(role) {
    return OWNER_INQUIRY_STAFF_ROLES.includes(role);
}
/**
 * ADR-0153 — ¿ve este rol el panel de encuestas del propietario? El
 * gerente_postventa (toda la org) y el jefe de proyecto (sus proyectos),
 * los de la bandeja de consultas (decisión 4 del founder), más el `admin`
 * (personal interno de BitácoraPro, encargado del tratamiento, para
 * soporte; figura en el ROPA). Ningún otro: ni `company_manager` (rol vivo
 * de facility y legacy retirado de postventa), ni los roles de facility,
 * ni el contratista (no tiene cuenta del dashboard). El alcance de filas
 * lo decide `loadScopedAssets`.
 */
export function canReadOwnerSurveys(role) {
    return isOwnerInquiryStaffRole(role) || role === "admin";
}
/** El rol que recibe la consulta cuando el proyecto no tiene jefe válido:
 *  todos los gerentes de postventa activos de la org. Lo lee auth-svc;
 *  nunca lo elige el llamador. */
export const OWNER_INQUIRY_FALLBACK_ROLE = "gerente_postventa";
/** Códigos de error que la PWA distingue. */
export const OWNER_INQUIRY_NO_RECIPIENT = "INQUIRY_NO_RECIPIENT";
export const OWNER_INQUIRY_NOT_DELIVERED = "INQUIRY_NOT_DELIVERED";
export const OWNER_INQUIRY_IN_PROGRESS = "INQUIRY_IN_PROGRESS";
/** El rol no puede ver la bandeja (staff) — 403. */
export const OWNER_INQUIRY_FORBIDDEN = "INQUIRY_FORBIDDEN";
/** La consulta no existe o está fuera del alcance de quien pregunta — 404. */
export const OWNER_INQUIRY_NOT_FOUND = "INQUIRY_NOT_FOUND";
/** ADR-0163 — la consulta pasó a observación y quedó sólo para leer: los
 *  POST de mensajes (los dos carriles) y las URL de subida responden 409
 *  con este código y `details: { findingId, folio }` (`folio: null` en el
 *  carril del propietario: regla 9 del spec). */
export const OWNER_INQUIRY_CONVERTED = "INQUIRY_CONVERTED";
/** Respuestas del staff por cuenta y minuto (POST .../messages). */
export const OWNER_INQUIRY_REPLY_RATE_LIMIT = 20;
/** Mensajes del propietario por cuenta y minuto (POST
 *  /assets/:id/inquiries/:inquiryId/messages, ADR-0155). */
export const OWNER_INQUIRY_OWNER_MESSAGE_RATE_LIMIT = 10;
/** Marcas de lectura por cuenta y minuto (POST .../read). */
export const OWNER_INQUIRY_READ_RATE_LIMIT = 60;
/** Las primeras ~140 letras del cuerpo, para la fila de la bandeja. */
export const OWNER_INQUIRY_EXCERPT_MAX = 140;
export const ownerInquiryCreateRequestSchema = z
    .object({
    body: z.string().trim().min(1).max(OWNER_INQUIRY_BODY_MAX),
})
    .strict();
/** Sólo existe con el correo entregado a al menos una persona de la
 *  inmobiliaria: si nadie la recibió, el POST es un error, no un 201. */
export const ownerInquiryCreateResponseSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string().datetime(),
    /** La fecha comprometida (10 días corridos en Santiago, mediodía UTC
     *  del día — `fechaCompromisoRespuesta`). La PWA la pinta con
     *  `fechaApoyoPropietario`, el correo con el mismo día calendario. */
    dueAt: z.string().datetime(),
});
/** GET /assets/:id/inquiries/availability — si alguien de la inmobiliaria
 *  puede recibir una consulta sobre esta unidad. false = la PWA oculta el
 *  botón «Nueva consulta» (decisión del fundador 2026-10-15); el historial
 *  se sigue mostrando. */
export const ownerInquiryAvailabilityResponseSchema = z.object({
    available: z.boolean(),
});
// ─── ADR-0145 — el hilo ──────────────────────────────────────────────
/** Estado de una consulta, DERIVADO en el servidor (no se guarda):
 *    - `sin_responder`: se espera respuesta del staff y está en plazo;
 *    - `vencida`: se espera respuesta y `now() > due_at`;
 *    - `respondida`: la última palabra la tiene el staff
 *      (`answered_at IS NOT NULL`, ADR-0155). Si el propietario vuelve a
 *      escribir, `answered_at` pasa a NULL y la consulta vuelve a
 *      `sin_responder` con un plazo nuevo. `first_response_at` ya no decide
 *      el estado: es la métrica de la primera respuesta.
 *  ADR-0163 — la única forma de cerrar un hilo es convertirlo en
 *  observación: la conversión fija `answered_at` y nadie vuelve a
 *  escribir, así que una consulta convertida queda `respondida` para
 *  siempre. «Pasó a observación» NO es un cuarto estado: los clientes lo
 *  pintan desde `conversion != null`.
 *  Nombres a propósito distintos de los de observaciones («abierta»,
 *  «resuelta», «rechazada»): la fitness observation-phase-single-source
 *  vigila esos literales. */
export const ownerInquiryEstadoSchema = z.enum(["sin_responder", "vencida", "respondida"]);
/** El texto visible de cada estado (es-CL). Única fuente: ninguna app lo
 *  escribe como literal. */
export const OWNER_INQUIRY_ESTADO_LABEL = {
    sin_responder: "Sin responder",
    vencida: "Vencida",
    respondida: "Respondida",
};
/**
 * Deriva el estado. `now` se inyecta (tests, seeds); el servidor pasa
 * `new Date()`. En el instante exacto `now == dueAt` la consulta todavía
 * está en plazo («antes del …» se cumple ese mismo instante): vencida es
 * estrictamente después.
 */
export function ownerInquiryEstado(row, now) {
    if (row.answeredAt !== null)
        return "respondida";
    const due = row.dueAt instanceof Date ? row.dueAt : new Date(row.dueAt);
    return now.getTime() > due.getTime() ? "vencida" : "sin_responder";
}
/**
 * ADR-0155 — qué le pasa a la consulta cuando el propietario escribe
 * (decisiones del founder 2026-10-16):
 *   - si estaba RESPONDIDA, se reabre: `answeredAt` pasa a null y el plazo
 *     se cuenta de nuevo desde su mensaje (`fechaCompromisoRespuesta`);
 *   - si todavía se esperaba respuesta (en plazo o vencida), nada cambia:
 *     el plazo vigente sigue, y una vencida sigue vencida.
 * Pura: la usan la ruta (sobre la fila bloqueada) y el seed.
 */
export function ownerInquiryAfterOwnerMessage(row, atIso) {
    if (row.answeredAt !== null) {
        return { reopened: true, answeredAt: null, dueAt: fechaCompromisoRespuesta(atIso) };
    }
    return { reopened: false, answeredAt: row.answeredAt, dueAt: row.dueAt };
}
/** Quién escribió una entrada. El mismo actor que el hilo de una
 *  observación: el staff recibe `{kind:"user", userId}` y resuelve el
 *  nombre contra `GET /auth/memberships`; el propietario recibe `self` u
 *  `organization` (proyección de `timeline-actor.ts`), nunca el id de un
 *  empleado. */
export const ownerInquiryActorSchema = findingTimelineActorSchema;
/** Una entrada del hilo. La entrada 0 es la consulta misma (`kind:
 *  "consulta"`, `id` = inquiryId, autor = el propietario, `createdAt` =
 *  el de la consulta); las demás son mensajes (`id` = messageId): `kind:
 *  "respuesta"` si lo escribió el staff y `"seguimiento"` si lo escribió
 *  el propietario (ADR-0155, por `author_lane`). Orden `(createdAt, seq)`
 *  ascendente, resuelto en el servidor: el cliente no reordena.
 *  ADR-0158: texto vacío ⇒ el mensaje trae adjuntos; la entrada 0 (la
 *  consulta) siempre trae `attachments: []`. */
export const ownerInquiryEntrySchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(["consulta", "respuesta", "seguimiento"]),
    author: ownerInquiryActorSchema,
    body: z.string().max(OWNER_INQUIRY_BODY_MAX),
    createdAt: z.string().datetime(),
    /** ADR-0158 — sólo metadatos; las URL firmadas se piden aparte. */
    attachments: z
        .array(ownerInquiryAttachmentSchema)
        .max(OWNER_INQUIRY_ATTACHMENTS_PER_MESSAGE_MAX)
        .default([]),
    /** ADR-0163 — sólo en el mensaje que escribió la conversión (`kind`
     *  sigue siendo «respuesta»): a qué observación pasó la consulta. El
     *  folio viaja sólo al personal: en el carril del propietario va null
     *  (regla 9 del spec, fitness F7: el propietario nunca ve folios), y
     *  también si la receta no usa folios. */
    conversion: z.object({ findingId: z.string().uuid(), folio: z.string().nullable() }).optional(),
});
/** Una fila de la bandeja (staff) o de la lista del propietario. */
export const ownerInquiryListItemSchema = z.object({
    id: z.string().uuid(),
    assetId: z.string().uuid(),
    /** Null cuando la unidad no tiene proyecto. */
    assetGroupId: z.string().uuid().nullable(),
    /** «{Proyecto} · Unidad {X}» (`propiedadLabel`), la misma etiqueta que
     *  la PWA y los correos. */
    propiedadLabel: z.string().min(1).max(300),
    /** Las primeras ~140 letras de la consulta. */
    excerpt: z
        .string()
        .min(1)
        .max(OWNER_INQUIRY_EXCERPT_MAX + 1),
    estado: ownerInquiryEstadoSchema,
    createdAt: z.string().datetime(),
    dueAt: z.string().datetime(),
    /** La primera respuesta del staff. Inmutable: métrica, no estado. */
    firstResponseAt: z.string().datetime().nullable(),
    /** ADR-0155 — desde cuándo la última palabra la tiene el staff (su
     *  último mensaje); null = se espera respuesta. Decide `estado`. */
    answeredAt: z.string().datetime().nullable(),
    /** `createdAt` de la última entrada (la consulta si no hay mensajes). */
    lastEntryAt: z.string().datetime(),
    /** Entradas del OTRO carril posteriores a la marca de quien pregunta
     *  (o todas, si nunca abrió el hilo). Para el staff, una consulta sin
     *  marca cuenta la consulta misma como 1. */
    unreadCount: z.number().int().nonnegative(),
    /** Sólo en el wire del staff: el propietario que preguntó, para
     *  resolver su nombre contra el roster. Nunca viaja al propietario. */
    authorUserId: z.string().uuid().optional(),
    /** ADR-0163 — la consulta pasó a observación y quedó sólo para leer
     *  («Pasó a observación» + el enlace), en los dos carriles. `folio` sólo
     *  en el del personal (su bandeja dice «Pasó a observación {folio}»): al
     *  propietario le llega null (regla 9 del spec). null = abierta.
     *  Opcional: un core-api anterior no lo trae. */
    conversion: z
        .object({
        findingId: z.string().uuid(),
        folio: z.string().nullable(),
        convertedAt: z.string().datetime(),
    })
        .nullable()
        .optional(),
});
export const ownerInquiryDetailSchema = z.object({
    inquiry: ownerInquiryListItemSchema,
    /** Orden `(createdAt, seq)` ascendente; la entrada 0 es la consulta. */
    entries: z.array(ownerInquiryEntrySchema).min(1),
    /** La marca PREVIA de quien pregunta (antes de esta lectura), para
     *  pintar «N nuevos»; null si nunca había abierto el hilo. El cliente
     *  marca la lectura con POST …/read después de pintar. */
    lastReadAt: z.string().datetime().nullable(),
    /** ADR-0163 — sólo en el wire del personal: ¿puede ESTA persona
     *  convertirla en observación? (jefe del proyecto o gerente de
     *  postventa, taxonomía y canal del propietario en la receta, sin
     *  conversión previa, el autor sigue siendo el dueño y la unidad está
     *  activada). Botones sin capacidad detrás no se muestran. */
    canConvert: z.boolean().optional(),
    /** ADR-0163 — `canConvert` y la sugerencia de la IA encendida en este
     *  ambiente (`INQUIRY_CONVERSION_SUGGESTION`). Sin ella, el jefe
     *  clasifica a mano y nada viaja a la IA. */
    canSuggest: z.boolean().optional(),
});
/** GET /owner-inquiries?assetGroupId= y GET /assets/:id/inquiries. */
export const ownerInquiryListResponseSchema = z.object({
    inquiries: z.array(ownerInquiryListItemSchema),
});
/** Alias del contrato: la bandeja del staff tiene la misma forma. */
export const ownerInquiryStaffListResponseSchema = ownerInquiryListResponseSchema;
export const ownerInquiryStaffListQuerySchema = z
    .object({
    assetGroupId: z.string().uuid(),
})
    .strict();
/** POST /owner-inquiries/:id/messages — la respuesta del staff. Requiere
 *  `Idempotency-Key`: un reintento con la misma clave devuelve el mismo
 *  mensaje sin reenviar el correo. ADR-0158: suma `attachments` (claves
 *  de nuestro presign); el texto puede ir vacío sólo si trae al menos un
 *  adjunto. */
export const ownerInquiryReplyRequestSchema = z
    .object({
    body: z.string().trim().max(OWNER_INQUIRY_BODY_MAX).default(""),
    attachments: z
        .array(ownerInquiryAttachmentRefSchema)
        .max(OWNER_INQUIRY_ATTACHMENTS_PER_MESSAGE_MAX)
        .default([]),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.body === "" && v.attachments.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["body"],
            message: "Escribe un mensaje o adjunta un archivo.",
        });
    }
    const keys = v.attachments.map((a) => a.objectKey);
    if (new Set(keys).size !== keys.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attachments"],
            message: "Hay un archivo repetido.",
        });
    }
});
export const ownerInquiryReplyResponseSchema = z.object({
    message: ownerInquiryEntrySchema,
    /** La fila ya con `estado: "respondida"` y `firstResponseAt`. */
    inquiry: ownerInquiryListItemSchema,
    /** El aviso «Respondimos tu consulta» le salió al propietario. false =
     *  no salió o no se sabe (timeout): el mensaje queda guardado igual. */
    ownerNotified: z.boolean(),
    /** ADR-0147 (corte de ajustes) — `suppressed_by_preference` cuando el
     *  propietario apagó el correo de «Consultas» (`ownerNotified` sigue en
     *  true: no es un fallo). Ausente en un replay (no se intentó). */
    ownerNotice: ownerNoticeOutcomeSchema.optional(),
    /** true sólo cuando el servidor devolvió el mensaje que ya había escrito
     *  con esa Idempotency-Key fuera del caché del plugin (la ruta además
     *  manda `Idempotency-Replayed: true`). En ese caso el aviso NO se
     *  reintenta y `ownerNotified: false` significa «no se intentó», no «falló»:
     *  el cliente no debe mostrar el aviso «no pudimos avisar». Ausente en una
     *  respuesta normal. */
    replayed: z.boolean().optional(),
});
/** ADR-0155 — POST /assets/:id/inquiries/:inquiryId/messages: el
 *  propietario vuelve a escribir en su consulta. Mismo cuerpo que la
 *  respuesta del staff; requiere `Idempotency-Key`. */
export const ownerInquiryOwnerMessageRequestSchema = ownerInquiryReplyRequestSchema;
/** El mensaje proyectado (autor `self`) y la fila ya con el estado y el
 *  plazo vigentes. Sin `authorUserId` y sin decir si se avisó al staff:
 *  eso no es asunto del propietario. */
export const ownerInquiryOwnerMessageResponseSchema = z.object({
    message: ownerInquiryEntrySchema,
    inquiry: ownerInquiryListItemSchema,
});
/** POST …/read — `lastReadAt` = el `createdAt` de la última entrada que
 *  se mostró, NUNCA el reloj del cliente. El servidor sólo avanza la
 *  marca. 204 sin cuerpo. */
export const ownerInquiryReadRequestSchema = z
    .object({
    lastReadAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=owner-inquiries.js.map